/**
 * R-131-SUP-A — client-side impersonation state + guards.
 */
import { getIdTokenResult, type User } from 'firebase/auth';

import { auth } from '../firebase';

export interface ImpersonatingClaim {
  actorUid: string;
  actorEmail: string;
  targetUid: string;
  startedAt: number;
  expiresAt: number;
  readOnly: boolean;
}

export interface ImpersonationState {
  active: boolean;
  targetUid: string;
  targetEmail: string | null;
  actorUid: string;
  actorEmail: string;
  expiresAt: number;
  readOnly: boolean;
  minutesRemaining: number;
}

export class ImpersonationReadOnlyError extends Error {
  constructor(message = 'Read-only impersonation — enable write mode or end the session.') {
    super(message);
    this.name = 'ImpersonationReadOnlyError';
  }
}

let cachedState: ImpersonationState | null = null;

export function setImpersonationCache(state: ImpersonationState | null): void {
  cachedState = state;
}

export function getImpersonationCache(): ImpersonationState | null {
  return cachedState;
}

export function parseImpersonatingClaim(raw: unknown): ImpersonatingClaim | null {
  if (!raw || typeof raw !== 'object') return null;
  const imp = raw as Partial<ImpersonatingClaim>;
  if (
    typeof imp.actorUid !== 'string'
    || typeof imp.actorEmail !== 'string'
    || typeof imp.targetUid !== 'string'
    || typeof imp.startedAt !== 'number'
    || typeof imp.expiresAt !== 'number'
    || typeof imp.readOnly !== 'boolean'
  ) {
    return null;
  }
  return imp as ImpersonatingClaim;
}

export function buildImpersonationState(
  user: User | null,
  claim: ImpersonatingClaim,
): ImpersonationState {
  const msLeft = Math.max(0, claim.expiresAt - Date.now());
  return {
    active: true,
    targetUid: claim.targetUid,
    targetEmail: user?.email ?? null,
    actorUid: claim.actorUid,
    actorEmail: claim.actorEmail,
    expiresAt: claim.expiresAt,
    readOnly: claim.readOnly,
    minutesRemaining: Math.ceil(msLeft / 60_000),
  };
}

export async function refreshImpersonationFromUser(
  user: User | null,
  forceRefresh = false,
): Promise<ImpersonationState | null> {
  if (!user) {
    setImpersonationCache(null);
    return null;
  }
  const tokenResult = await getIdTokenResult(user, forceRefresh);
  const claim = parseImpersonatingClaim(tokenResult.claims.impersonating);
  if (!claim) {
    setImpersonationCache(null);
    return null;
  }
  if (Date.now() > claim.expiresAt) {
    setImpersonationCache(null);
    return null;
  }
  const state = buildImpersonationState(user, claim);
  setImpersonationCache(state);
  return state;
}

export function assertCanMutate(context?: string): void {
  const state = getImpersonationCache();
  if (state?.active && state.readOnly) {
    throw new ImpersonationReadOnlyError(
      context
        ? `Read-only impersonation — cannot ${context}.`
        : undefined,
    );
  }
}

export function isImpersonationReadOnly(): boolean {
  return Boolean(getImpersonationCache()?.active && getImpersonationCache()?.readOnly);
}

const IMPERSONATION_ACTOR_KEY = 'impersonation_actor_uid';

/** Stash actor uid so sign-out during impersonation can trigger end audit. */
export function stashImpersonationActor(actorUid: string): void {
  try {
    sessionStorage.setItem(IMPERSONATION_ACTOR_KEY, actorUid);
  } catch {
    // sessionStorage unavailable
  }
}

export function clearImpersonationStash(): void {
  try {
    sessionStorage.removeItem(IMPERSONATION_ACTOR_KEY);
  } catch {
    // sessionStorage unavailable
  }
}

export function readImpersonationStash(): string | null {
  try {
    return sessionStorage.getItem(IMPERSONATION_ACTOR_KEY);
  } catch {
    return null;
  }
}

/** Exchange custom token and land on home as the target user. */
export async function signInWithImpersonationToken(token: string): Promise<void> {
  const { signInWithCustomToken } = await import('firebase/auth');
  await signInWithCustomToken(auth, token);
}
